/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: 通用图片对比滑块组件 - 支持左右拖拽对比
 */
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface ImageCompareItem {
  id: string;
  beforeUrl: string;
  afterUrl: string;
  thumbnailUrl: string;
  label?: string;
}

export interface ImageCompareSliderProps {
  images: ImageCompareItem[];
  defaultIndex?: number;
  className?: string;
  /** 主题色，用于选中边框等 */
  themeColor?: string;
}

// 拖拽手柄内的左箭头图标
const SliderLeftArrowIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="12"
    viewBox="0 0 12 16"
    fill="none"
  >
    <path d="M0 8L12 0V16L0 8Z" fill="black" />
    <path d="M0 8L12 0V16L0 8Z" fill="black" fillOpacity="0.2" />
  </svg>
);

// 拖拽手柄内的右箭头图标
const SliderRightArrowIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="12"
    viewBox="0 0 12 16"
    fill="none"
  >
    <path d="M12 8L0 0V16L12 8Z" fill="black" />
  </svg>
);

// 拖拽对比手柄容器
const CompareHandle: React.FC = () => (
  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center gap-1">
    <SliderLeftArrowIcon />
    <SliderRightArrowIcon />
  </div>
);

const ImageCompareSlider: React.FC<ImageCompareSliderProps> = ({
  images,
  defaultIndex = 0,
  className = '',
  themeColor = '#6366F1',
}) => {
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showNavigation, setShowNavigation] = useState(false);

  const currentImage = images[currentIndex];

  // 切换到上一张
  const previousImage = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setSliderPosition(50);
  }, [images.length]);

  // 切换到下一张
  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setSliderPosition(50);
  }, [images.length]);

  // 切换到指定图片
  const changeImage = useCallback((index: number) => {
    setCurrentIndex(index);
    setSliderPosition(50);
  }, []);

  // 处理拖拽
  const handleMove = useCallback(
    (clientX: number) => {
      if (!containerRef.current || !isDragging) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    },
    [isDragging],
  );

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      handleMove(e.clientX);
    },
    [handleMove],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    },
    [handleMove],
  );

  // 绑定全局事件
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  // 检查是否需要显示导航按钮
  const checkScrollable = useCallback(() => {
    if (carouselRef.current) {
      const { scrollWidth, clientWidth } = carouselRef.current;
      setShowNavigation(scrollWidth > clientWidth);
    }
  }, []);

  // 监听容器尺寸变化
  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);

    const resizeObserver = new ResizeObserver(checkScrollable);
    if (carouselRef.current) {
      resizeObserver.observe(carouselRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkScrollable);
      resizeObserver.disconnect();
    };
  }, [checkScrollable]);

  // 滚动到选中的图片
  useEffect(() => {
    if (carouselRef.current) {
      const selectedElement = carouselRef.current.children[currentIndex] as HTMLElement;
      if (selectedElement) {
        const containerRect = carouselRef.current.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();
        const scrollLeft =
          selectedElement.offsetLeft - containerRect.width / 2 + elementRect.width / 2;
        carouselRef.current.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        });
      }
    }
  }, [currentIndex]);

  // 左右滚动控制
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 120;
      const newScrollLeft =
        carouselRef.current.scrollLeft +
        (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  if (!images.length) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-4 h-full ${className}`}>
      {/* 图片对比展示区域 - 外层容器用于居中 */}
      <div className="relative w-full flex-1 min-h-0 overflow-hidden rounded-2xl bg-white select-none flex items-center justify-center">
        {/* 内层容器 - 保持图片原始比例 */}
        <div
          ref={containerRef}
          className="relative w-full h-full max-w-full max-h-full"
          style={{ aspectRatio: '16/9' }}
        >
          {/* After 图片 (底层) */}
          {currentImage && (
            <Image
              key={`after-${currentImage.id}`}
              src={currentImage.afterUrl}
              alt="After"
              fill
              className="object-cover"
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
              draggable={false}
            />
          )}

          {/* Before 图片 (上层，通过 clip 裁切) */}
          {currentImage && (
            <div
              key={`before-${currentImage.id}`}
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <Image
                src={currentImage.beforeUrl}
                alt="Before"
                fill
                className="object-cover"
                priority
                sizes="(max-width: 768px) 100vw, 50vw"
                draggable={false}
              />
            </div>
          )}

          {/* 拖拽分割线 */}
          <div
            className="absolute top-0 bottom-0 bg-white cursor-ew-resize z-10"
            style={{
              width: '4px',
              left: `${sliderPosition}%`,
              transform: 'translateX(-50%)',
              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
          >
            {/* 拖拽手柄 */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
            >
              <CompareHandle />
            </div>
          </div>

          {/* 控制按钮 - 右上角 */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            {/* 上一个按钮 */}
            <button
              type="button"
              onClick={previousImage}
              className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md flex items-center justify-center transition-all duration-300 shadow-lg sm:w-9 sm:h-9"
              aria-label="Previous"
            >
              <LeftOutlined className="text-white text-sm" />
            </button>

            {/* 下一个按钮 */}
            <button
              type="button"
              onClick={nextImage}
              className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md flex items-center justify-center transition-all duration-300 shadow-lg sm:w-9 sm:h-9"
              aria-label="Next"
            >
              <RightOutlined className="text-white text-sm" />
            </button>
          </div>
        </div>
      </div>

      {/* 图片选择器 */}
      <div className="flex items-center justify-center gap-2">
        {/* 左箭头 */}
        {showNavigation && (
          <button
            type="button"
            onClick={() => scrollCarousel('left')}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Scroll left"
          >
            <svg width="8" height="12" viewBox="0 0 8 12" fill="currentColor">
              <path d="M7 1L2 6L7 11" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </button>
        )}

        {/* 图片缩略图滚动容器 */}
        <div
          ref={carouselRef}
          className="flex gap-3 overflow-x-auto py-2 scroll-smooth sm:gap-2 justify-center"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {images.map((image, index) => (
            <button
              key={image.id}
              type="button"
              onClick={() => changeImage(index)}
              className="flex-shrink-0 w-[100px] h-[60px] rounded-xl overflow-hidden border-2 transition-all duration-200 hover:scale-105 sm:w-[80px] sm:h-[48px]"
              style={{
                borderColor: index === currentIndex ? themeColor : 'transparent',
                boxShadow: index === currentIndex ? `0 4px 12px ${themeColor}30` : 'none',
              }}
              aria-label={`Select image ${index + 1}`}
            >
              <div className="relative w-full h-full">
                <Image
                  src={image.thumbnailUrl}
                  alt={`Thumbnail ${index + 1}`}
                  fill
                  className="object-cover"
                  sizes="100px"
                />
              </div>
            </button>
          ))}
        </div>

        {/* 右箭头 */}
        {showNavigation && (
          <button
            type="button"
            onClick={() => scrollCarousel('right')}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Scroll right"
          >
            <svg width="8" height="12" viewBox="0 0 8 12" fill="currentColor">
              <path d="M1 1L6 6L1 11" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
          </button>
        )}
      </div>

      {/* 隐藏滚动条样式 */}
      <style jsx>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ImageCompareSlider;
